import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { inlineEditKey } from '@/components/inline-edit-keys'
import { useCloseAfterPress } from '@/components/use-close-after-press'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingButton } from '@/components/loading-button'
import { useUpdateLineItem, useApplyDiscountChainToAll } from '@/features/purchase-orders/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatMoney } from '@/lib/utils'
import type { PurchaseOrderLineItemRead } from './types'
import { isManufacturerSurchargeLine } from './line-item-policy'

export function EditableLineItemProductCell({
  poUuid,
  lineItem,
  showManufacturer,
  imageClassName,
  modelSuffix,
}: {
  poUuid: string
  lineItem: PurchaseOrderLineItemRead
  showManufacturer: boolean
  imageClassName?: string
  modelSuffix?: ReactNode
}) {
  const mutation = useUpdateLineItem(poUuid)
  const save = (field: 'model' | 'description', value: string) => {
    const text = value.trim()
    if (field === 'model' && !text) {
      toast.error('Model cannot be blank')
      return
    }
    if (text === lineItem[field]) return
    mutation.mutate(
      { lineItemId: lineItem.id, [field]: text },
      {
        onError: (error) =>
          toast.error(
            error && typeof error === 'object' && 'detail' in error
              ? String(error.detail)
              : 'Could not save line text. Please retry.',
          ),
      },
    )
  }
  return (
    <POLineItemProductCell
      model={lineItem.model}
      description={lineItem.description}
      productUuid={lineItem.product?.uuid}
      imageUrl={lineItem.product?.image_url}
      manufacturerName={lineItem.manufacturer_name || lineItem.product?.manufacturer_name}
      manufacturerUuid={lineItem.product?.manufacturer_uuid}
      showManufacturer={showManufacturer}
      imageClassName={imageClassName}
      modelSuffix={modelSuffix}
      modelContent={
        <div className="flex min-w-0 items-center gap-1">
          <POInlineEditableField
            value={lineItem.model}
            displayValue={mutation.isPending ? mutation.variables?.model : undefined}
            ariaLabel={`Edit model for line ${lineItem.display_number || lineItem.item_number}`}
            title="Edit model"
            pending={mutation.isPending}
            showEditIcon
            allowWrap
            buttonClassName="-ml-2 font-medium [overflow-wrap:anywhere]"
            inputClassName="min-h-10"
            onConfirm={(value) => save('model', value)}
          />
          {lineItem.product?.uuid && (
            <Link
              to="/products/$productId"
              params={{ productId: lineItem.product.uuid }}
              aria-label={`View linked product for line ${lineItem.display_number || lineItem.item_number}`}
              title="View linked product"
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-md text-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <ExternalLink className="size-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      }
      descriptionContent={
        <POInlineEditableField
          value={lineItem.description}
          displayValue={mutation.isPending ? mutation.variables?.description : undefined}
          kind="textarea"
          ariaLabel={`Edit specifications for line ${lineItem.display_number || lineItem.item_number}`}
          title="Edit specifications"
          placeholder="Add specifications"
          pending={mutation.isPending}
          showEditIcon
          allowWrap
          buttonClassName="-ml-2 text-xs [overflow-wrap:anywhere] [&>span]:line-clamp-2"
          inputClassName="w-full min-h-28 field-sizing-fixed"
          onConfirm={(value) => save('description', value)}
        />
      }
    />
  )
}

export function EditableDiscountCell({
  poUuid,
  lineItem,
  hideDefault = false,
}: {
  poUuid: string
  lineItem: PurchaseOrderLineItemRead
  /** No catalog default beside it: a unit from our own warehouse costs its lot's Net. */
  hideDefault?: boolean
}) {
  const [editing, setEditing] = useState(false)
  // A cell blurred by a press closes after the press's click (USA-1647).
  const closeAfterPress = useCloseAfterPress(editing)
  const [value, setValue] = useState(lineItem.discount_display || '')
  const [confirmApplyAll, setConfirmApplyAll] = useState(false)
  const updateLineItem = useUpdateLineItem(poUuid)
  const applyToAll = useApplyDiscountChainToAll(poUuid)
  const defaultChain = lineItem.product?.default_discount_chain
  const isPending = updateLineItem.isPending && updateLineItem.variables?.lineItemId === lineItem.id
  const canEdit = (lineItem.list_price ?? null) !== null && Number(lineItem.list_price) > 0

  const commit = () => {
    const trimmed = value.trim()
    if (!trimmed || trimmed === (lineItem.discount_display || '')) {
      closeAfterPress(() => setEditing(false))
      setValue(lineItem.discount_display || '')
      return
    }
    updateLineItem.mutate(
      { lineItemId: lineItem.id, discountChain: trimmed },
      {
        onSuccess: () => {
          toast.success('Discount updated')
          closeAfterPress(() => setEditing(false))
        },
        onError: (err) => {
          const detail =
            err && typeof err === 'object' && 'detail' in err
              ? String((err as { detail: unknown }).detail)
              : 'Failed to update discount'
          toast.error(detail)
          setValue(lineItem.discount_display || '')
        },
      },
    )
  }

  const cancel = () => {
    setEditing(false)
    setValue(lineItem.discount_display || '')
  }

  const trimmedChain = value.trim()
  const canApplyToAll = editing && trimmedChain.length > 0 && !isPending

  const applyAll = () => {
    applyToAll.mutate(
      { discountChain: trimmedChain },
      {
        onSuccess: (data) => {
          toast.success(`Applied ${trimmedChain} to ${data?.items_updated ?? 0} line items`)
          setConfirmApplyAll(false)
          setEditing(false)
        },
        onError: (err) => {
          const detail =
            err && typeof err === 'object' && 'detail' in err
              ? String((err as { detail: unknown }).detail)
              : 'Failed to apply discount to all line items'
          toast.error(detail)
        },
      },
    )
  }

  return (
    <div className="flex min-w-0 flex-col items-end gap-0.5">
      {editing ? (
        <>
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => {
              // Don't commit if focus is moving to the "Apply to all" button.
              const next = e.relatedTarget as HTMLElement | null
              if (next?.dataset.applyAll === 'true') return
              commit()
            }}
            onKeyDown={(e) => inlineEditKey(e, commit, cancel)}
            disabled={isPending}
            placeholder="50/10/5"
            className="h-7 w-24 text-right text-sm"
          />
          <button
            type="button"
            data-apply-all="true"
            disabled={!canApplyToAll}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setConfirmApplyAll(true)}
            className="text-[10px] leading-tight text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            apply to all
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={!canEdit || isPending}
          onClick={() => canEdit && setEditing(true)}
          title={canEdit ? 'Click to edit discount chain' : 'No list price — cannot apply chain'}
          className="-mr-1.5 rounded px-1.5 py-0.5 text-right text-sm tabular-nums hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
        >
          {isPending ? '…' : lineItem.discount_display || '-'}
        </button>
      )}
      {!hideDefault && defaultChain && defaultChain !== (lineItem.discount_display || '') && (
        <span
          className="text-[10px] leading-tight text-muted-foreground/70"
          title="Current default discount for this product in our system"
        >
          default: {defaultChain}
        </span>
      )}

      <Dialog
        open={confirmApplyAll}
        onOpenChange={(open) => !applyToAll.isPending && setConfirmApplyAll(open)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Apply discount to every line item?</DialogTitle>
            <DialogDescription>
              Every line item with a list price will be repriced using{' '}
              <span className="font-medium">{trimmedChain}</span>. The PO total and PDF will be
              regenerated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmApplyAll(false)}
              disabled={applyToAll.isPending}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={applyToAll.isPending}
              loadingText="Applying..."
              onClick={applyAll}
            >
              Apply to all
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function EditableUnitPriceCell({
  poUuid,
  lineItem,
}: {
  poUuid: string
  lineItem: PurchaseOrderLineItemRead
}) {
  const [editing, setEditing] = useState(false)
  // A cell blurred by a press closes after the press's click (USA-1647).
  const closeAfterPress = useCloseAfterPress(editing)
  const [value, setValue] = useState(lineItem.unit_price?.toString() || '')
  const updateLineItem = useUpdateLineItem(poUuid)
  const isPending = updateLineItem.isPending && updateLineItem.variables?.lineItemId === lineItem.id
  const isSurcharge = isManufacturerSurchargeLine(lineItem)

  const commit = () => {
    const trimmed = value.trim()
    if (!trimmed || trimmed === (lineItem.unit_price?.toString() || '')) {
      closeAfterPress(() => setEditing(false))
      setValue(lineItem.unit_price?.toString() || '')
      return
    }
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed) || parsed < 0 || (isSurcharge && parsed === 0)) {
      toast.error(
        isSurcharge
          ? 'Surcharge amount must be greater than zero'
          : 'Unit price must be non-negative',
      )
      setValue(lineItem.unit_price?.toString() || '')
      return
    }
    updateLineItem.mutate(
      { lineItemId: lineItem.id, unitPrice: trimmed },
      {
        onSuccess: () => {
          toast.success(isSurcharge ? 'Surcharge amount updated' : 'Unit price updated')
          closeAfterPress(() => setEditing(false))
        },
        onError: (err) => {
          const detail =
            err && typeof err === 'object' && 'detail' in err
              ? String((err as { detail: unknown }).detail)
              : 'Failed to update unit price'
          toast.error(detail)
          setValue(lineItem.unit_price?.toString() || '')
        },
      },
    )
  }

  const cancel = () => {
    setEditing(false)
    setValue(lineItem.unit_price?.toString() || '')
  }

  if (editing) {
    return (
      <Input
        autoFocus
        type="number"
        step="0.01"
        min={isSurcharge ? '0.01' : '0'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => inlineEditKey(e, commit, cancel)}
        disabled={isPending}
        className="h-7 w-24 text-right text-sm"
      />
    )
  }

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => setEditing(true)}
      title={
        isSurcharge
          ? 'Click to edit surcharge amount'
          : 'Click to edit unit price (sets discount to =Net=)'
      }
      className="-mr-1.5 rounded px-1.5 py-0.5 text-sm tabular-nums hover:bg-accent disabled:opacity-60 disabled:hover:bg-transparent"
    >
      {isPending ? '…' : formatMoney(lineItem.unit_price)}
    </button>
  )
}

export function EditableQtyCell({
  poUuid,
  lineItem,
}: {
  poUuid: string
  lineItem: PurchaseOrderLineItemRead
}) {
  const [editing, setEditing] = useState(false)
  // A cell blurred by a press closes after the press's click (USA-1647).
  const closeAfterPress = useCloseAfterPress(editing)
  const [value, setValue] = useState(String(lineItem.quantity))
  const updateLineItem = useUpdateLineItem(poUuid)
  const isPending = updateLineItem.isPending && updateLineItem.variables?.lineItemId === lineItem.id

  const commit = () => {
    const trimmed = value.trim()
    const parsed = parseInt(trimmed, 10)
    if (!trimmed || parsed === lineItem.quantity) {
      closeAfterPress(() => setEditing(false))
      setValue(String(lineItem.quantity))
      return
    }
    if (!Number.isFinite(parsed) || parsed < 1) {
      toast.error('Quantity must be a positive integer')
      setValue(String(lineItem.quantity))
      return
    }
    updateLineItem.mutate(
      { lineItemId: lineItem.id, quantity: parsed },
      {
        onSuccess: () => {
          toast.success('Quantity updated')
          closeAfterPress(() => setEditing(false))
        },
        onError: (err) => {
          const detail =
            err && typeof err === 'object' && 'detail' in err
              ? String((err as { detail: unknown }).detail)
              : 'Failed to update quantity'
          toast.error(detail)
          setValue(String(lineItem.quantity))
        },
      },
    )
  }

  const cancel = () => {
    setEditing(false)
    setValue(String(lineItem.quantity))
  }

  if (editing) {
    return (
      <Input
        autoFocus
        type="number"
        inputMode="numeric"
        step="1"
        min="1"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => inlineEditKey(e, commit, cancel)}
        disabled={isPending}
        className="h-7 w-16 text-right text-sm"
      />
    )
  }

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => setEditing(true)}
      title="Click to edit quantity"
      className="-mr-1.5 rounded px-1.5 py-0.5 text-sm tabular-nums hover:bg-accent disabled:opacity-60 disabled:hover:bg-transparent"
    >
      {isPending ? '…' : lineItem.quantity}
    </button>
  )
}

export function EditableListPriceCell({
  poUuid,
  lineItem,
}: {
  poUuid: string
  lineItem: PurchaseOrderLineItemRead
}) {
  const [editing, setEditing] = useState(false)
  // A cell blurred by a press closes after the press's click (USA-1647).
  const closeAfterPress = useCloseAfterPress(editing)
  const [value, setValue] = useState(lineItem.list_price?.toString() || '')
  const updateLineItem = useUpdateLineItem(poUuid)
  const isPending = updateLineItem.isPending && updateLineItem.variables?.lineItemId === lineItem.id

  const commit = () => {
    const trimmed = value.trim()
    if (!trimmed || trimmed === (lineItem.list_price?.toString() || '')) {
      closeAfterPress(() => setEditing(false))
      setValue(lineItem.list_price?.toString() || '')
      return
    }
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error('List price must be a non-negative number')
      setValue(lineItem.list_price?.toString() || '')
      return
    }
    updateLineItem.mutate(
      { lineItemId: lineItem.id, listPrice: trimmed },
      {
        onSuccess: () => {
          toast.success('List price updated')
          closeAfterPress(() => setEditing(false))
        },
        onError: (err) => {
          const detail =
            err && typeof err === 'object' && 'detail' in err
              ? String((err as { detail: unknown }).detail)
              : 'Failed to update list price'
          toast.error(detail)
          setValue(lineItem.list_price?.toString() || '')
        },
      },
    )
  }

  const cancel = () => {
    setEditing(false)
    setValue(lineItem.list_price?.toString() || '')
  }

  if (editing) {
    return (
      <Input
        autoFocus
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => inlineEditKey(e, commit, cancel)}
        disabled={isPending}
        className="h-7 w-24 text-right text-sm"
      />
    )
  }

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => setEditing(true)}
      title="Click to edit list price"
      className="-mr-1.5 rounded px-1.5 py-0.5 text-sm tabular-nums hover:bg-accent disabled:opacity-60 disabled:hover:bg-transparent"
    >
      {isPending ? '…' : formatMoney(lineItem.list_price)}
    </button>
  )
}
